import LabelBackground from '@/components/LabelBackground/LabelBackground';
import useHaptics from '@/hooks/useHaptics';
import cn from '@/lib/cs';
import { motion } from 'framer-motion';
import { ReactNode } from 'react';

const Button = ({
	type = 'button',
	className,
	backgroundClassName,
	textClassName,
	icon,
	label,
	image,
	animationDelay = 0,
	animate = {},
	initial = {},
	...props
}: React.ComponentProps<typeof motion.button> & {
	backgroundClassName?: string;
	textClassName?: string;
	label: string;
	image?: {
		src: string;
		alt: string;
	};
	icon?: ReactNode;
	animationDelay?: number;
}) => {
	const { withOnButtonPress } = useHaptics();

	return (
		<motion.button
			type={type}
			className={cn(
				'rounded-full cursor-pointer disabled:cursor-not-allowed',
				className,
			)}
			initial={{
				scale: 0,
				...(typeof initial === 'object' ? initial : {}),
			}}
			animate={{
				scale: 1,
				transition: {
					delay: animationDelay,
				},
				...(typeof animate === 'object' ? animate : {}),
			}}
			whileTap={{
				scale: 0.95,
			}}
			aria-label={label}
			{...withOnButtonPress}
			{...props}>
			<div className="relative flex items-center py-1">
				<LabelBackground
					className={cn('text-gray-950', backgroundClassName)}
				/>
				{image && (
					<div className="relative shrink-0 ps-2">
						<img
							src={image.src}
							alt={image.alt}
							className="size-12 rounded-full object-cover"
						/>
					</div>
				)}
				<div
					className={cn(
						'relative text-gray-50 font-brand text-2xl ps-2',
						textClassName,
					)}>
					{label}
				</div>
				{!!icon && (
					<div
						className={cn(
							'relative shrink-0 grow-0 text-gray-50 scale-50',
							textClassName,
						)}
						aria-hidden>
						{icon}
					</div>
				)}
			</div>
		</motion.button>
	);
};

export default Button;
